import React, { useState, useEffect } from 'react';
import { Standard, Student } from '../../../data/types';
import { GameIcons } from '../../styles/GameIcons';
import '../../styles/GameStyleUI.css';
import { motion } from 'framer-motion';

interface LearningPhaseResultsProps {
  score: { correct: number; total: number };
  pointsEarned: number;
  standard: Standard | null;
  student: Student | null;
  onExit: () => void;
}

const LearningPhaseResults: React.FC<LearningPhaseResultsProps> = ({ 
  score, 
  pointsEarned, 
  standard, 
  student,
  onExit 
}) => {
  const [leaderboard, setLeaderboard] = useState<{name: string, points: number}[]>([]);
  const [showConfetti, setShowConfetti] = useState(true);
  
  // Load leaderboard data
  useEffect(() => {
    // Simulate fetching leaderboard data
    // This would normally come from an API
    const mockLeaderboard = [
      { name: student?.name || 'You', points: student?.totalPoints ? student.totalPoints + pointsEarned : pointsEarned },
      { name: 'Alex', points: 430 },
      { name: 'Morgan', points: 385 },
      { name: 'Jordan', points: 350 },
      { name: 'Taylor', points: 310 }
    ];
    
    // Sort by points
    const sortedLeaderboard = [...mockLeaderboard].sort((a, b) => b.points - a.points);
    setLeaderboard(sortedLeaderboard);
    
    // Turn off confetti after 3 seconds
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [student, pointsEarned]);
  
  // Calculate success message based on score
  const getSuccessMessage = () => {
    const percentage = score.total > 0 ? (score.correct / score.total) * 100 : 100;
    
    if (percentage >= 90) return "Outstanding job! You've mastered this concept!";
    if (percentage >= 70) return "Great work! You're understanding this concept well!";
    if (percentage >= 50) return "Good effort! You're making progress on this concept.";
    return "Nice try! Keep practicing to improve on this concept.";
  };
  
  // Helper function to generate consistent avatar emojis based on name
  const getAvatarEmoji = (name: string) => {
    const emojiList = ['😀', '😃', '😄', '😁', '😆', '😊', '🙂', '😎', '🤩', '🥳', '🦊', '🦁', '🐯', '🐱', '🐶'];
    
    // Use the name to create a deterministic index
    let sum = 0;
    for (let i = 0; i < name.length; i++) {
      sum += name.charCodeAt(i);
    }
    
    return emojiList[sum % emojiList.length];
  };

  return (
    <div className="bg-gradient-to-b from-blue-50 to-indigo-50 p-6 rounded-xl relative">
      {/* Confetti effect */}
      {showConfetti && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
          {Array.from({ length: 100 }).map((_, i) => (
            <motion.div 
              key={i}
              initial={{ 
                y: -20, 
                x: Math.random() * 100 - 50 + (window.innerWidth / 2), 
                opacity: 1, 
                scale: Math.random() * 0.5 + 0.5 
              }}
              animate={{ 
                y: window.innerHeight, 
                x: Math.random() * window.innerWidth, 
                opacity: 0, 
                rotate: Math.random() * 360 
              }}
              transition={{ duration: Math.random() * 3 + 1, ease: "easeOut" }}
              className="absolute w-3 h-3"
              style={{ 
                backgroundColor: ['#FF5E5B', '#4F86C6', '#FFCB47', '#44BBA4', '#FF9505'][Math.floor(Math.random() * 5)],
                borderRadius: Math.random() > 0.5 ? '50%' : '0%'
              }}
            />
          ))}
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center">
          <GameIcons.Trophy className="mr-2" size={24} color="#4F46E5" />
          <span>Results & Rewards</span>
        </h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Results summary */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-lg p-6 order-1 md:order-1"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <GameIcons.Check className="mr-2" size={20} color="#4338CA" />
            <span>Activity Complete!</span>
          </h3>
          
          <div className="bg-indigo-50 rounded-lg p-5 mb-6">
            <h4 className="font-bold text-indigo-800 mb-3">Standard Details</h4>
            <div className="flex flex-col space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Standard:</span>
                <span className="font-medium text-indigo-700">{standard?.code}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Title:</span>
                <span className="font-medium text-indigo-700">{standard?.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Grade Level:</span>
                <span className="font-medium text-indigo-700">{standard?.gradeLevel}</span>
              </div>
            </div>
          </div>
          
          <div className="mb-6">
            <h4 className="font-bold text-gray-800 mb-3">Your Performance</h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-gray-500 text-sm mb-1">Quiz Score</p>
                <p className="text-2xl font-bold text-blue-600">
                  {score.correct}/{score.total}
                </p>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <p className="text-gray-500 text-sm mb-1">Game Score</p>
                <p className="text-2xl font-bold text-green-600">
                  {pointsEarned - (score.correct * 5)}
                </p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <p className="text-gray-500 text-sm mb-1">Points Earned</p>
                <p className="text-2xl font-bold text-purple-600">
                  +{pointsEarned}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg p-4 text-white">
            <p className="font-medium text-center">
              {getSuccessMessage()}
            </p>
          </div>
        </motion.div>
        
        {/* Leaderboard preview */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 order-2 md:order-2"
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <GameIcons.Crown className="mr-2" size={20} color="#4338CA" />
            <span>Activity Leaderboard</span>
          </h3>
          
          <div className="space-y-3 mb-6">
            {leaderboard.map((entry, index) => (
              <div 
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  entry.name === student?.name || entry.name === 'You'
                    ? 'bg-indigo-50 border border-indigo-200'
                    : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                    index === 0 
                      ? 'bg-yellow-500 text-white'
                      : index === 1
                        ? 'bg-gray-400 text-white'
                        : index === 2
                          ? 'bg-amber-700 text-white'
                          : 'bg-indigo-100 text-indigo-800'
                  }`}>
                    {index < 3 
                      ? <GameIcons.Medal size={16} color="currentColor" />
                      : <span>{index + 1}</span>
                    }
                  </div>
                  
                  {/* Student avatar */}
                  <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white flex items-center justify-center bg-indigo-100 mr-2">
                    <span className="text-xl select-none">
                      {getAvatarEmoji(entry.name)}
                    </span>
                  </div>
                  
                  <span className={`font-medium ${
                    entry.name === student?.name || entry.name === 'You'
                      ? 'text-indigo-800'
                      : 'text-gray-800'
                  }`}>
                    {entry.name}
                    {(entry.name === student?.name || entry.name === 'You') && ' (You)'}
                  </span>
                </div>
                <div className="font-bold text-indigo-600">{entry.points} pts</div>
              </div>
            ))}
          </div>
          
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-6">
            <div className="flex">
              <GameIcons.LightBulb size={20} className="mr-3 flex-shrink-0 mt-1" color="#B45309" />
              <div>
                <h4 className="font-bold text-yellow-800 mb-1">Learning Tip</h4>
                <p className="text-yellow-700 text-sm">
                  Try practicing this standard again to improve your score and earn more points! 
                  Regular practice leads to better understanding and higher achievement.
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={() => window.open('/student/leaderboard', '_blank')}
            className="w-full px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center justify-center"
          >
            <GameIcons.ChartBar size={18} className="mr-2" color="currentColor" />
            <span>View Full Leaderboard</span>
          </button>
        </motion.div>
      </div>
      
      {/* Action buttons */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4 mt-6"
      >
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors flex items-center justify-center"
        >
          <GameIcons.RefreshCw size={18} className="mr-2" color="currentColor" />
          <span>Practice Again</span>
        </button>
        
        <button
          onClick={onExit}
          className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg shadow-md flex items-center justify-center"
        >
          <span>Back to Dashboard</span>
          <GameIcons.Home size={18} className="ml-2" color="currentColor" />
        </button>
      </motion.div>
      
      {/* Auto-save indicator */}
      <div className="mt-4 text-center">
        <span className="inline-flex items-center text-xs text-green-600">
          <GameIcons.CloudCheck size={14} className="mr-1" color="currentColor" />
          Your progress has been saved
        </span>
      </div>
    </div>
  );
};

export default LearningPhaseResults;
